// src/pages/validasi/ValidasiLevel1.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, XCircle, RotateCcw, Eye, AlertTriangle, Filter, Search,
  Clock, FileText, ChevronDown, ChevronUp, User, Building2,
  TrendingUp, TrendingDown, Calendar, History, Info, Shield
} from 'lucide-react';
import { Button, Badge, Card, Breadcrumb, Modal, Textarea, Select, Input, Loading } from '../../components/ui';
import { LaporanMasalah } from '../../types';
import { Link } from 'react-router-dom';
import { validasiService, ValidasiFilter } from '../../services/validasi';
import { workflowService } from '../../services/workflow';
import { useRole } from '../../hooks/useRole';
import { getErrorMessage, logError } from '../../utils/errorHandler';
import toast from 'react-hot-toast';

type ValidasiAction = 'approve' | 'reject' | 'revisi' | 'resolve';

interface Stats {
  pending_level1: number;
  pending_level2: number;
  approved_today_l1: number;
  approved_today_l2: number;
  rejected_today_l1: number;
  rejected_today_l2: number;
}

const ValidasiLevel1: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending');
  const [laporan, setLaporan] = useState<LaporanMasalah[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLaporan, setSelectedLaporan] = useState<LaporanMasalah | null>(null);
  const [action, setAction] = useState<ValidasiAction | null>(null);
  const [catatan, setCatatan] = useState('');
  const [processing, setProcessing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ValidasiFilter>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [canResolveMap, setCanResolveMap] = useState<Record<number, boolean>>({});

  const role = useRole();

  const fetchResolveAbility = async (items: LaporanMasalah[]) => {
    const map: Record<number, boolean> = {};
    const batchSize = 5;
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      await Promise.all(
        batch.map(async (lap) => {
          try {
            const summary = await workflowService.getSummary(lap.id);
            map[lap.id] = summary?.can_resolve === true;
          } catch {
            map[lap.id] = false;
          }
        })
      );
    }
    setCanResolveMap(prev => ({ ...prev, ...map }));
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params: ValidasiFilter = { ...filters };
      if (searchTerm) params.search = searchTerm;

      let res;
      if (activeTab === 'pending') {
        // ✅ FIX: Ambil baik status 'diajukan' maupun 'validasi_l1'
        // untuk menangani laporan yang skip step 1 atau sedang transisi
        params.status = 'diajukan,validasi_l1';
        res = await validasiService.getPendingLevel1(params);
      } else {
        res = await validasiService.getRiwayatLevel1(params);
      }

      const data = (res as any)?.data || res;
      const items = Array.isArray(data) ? data : (data?.data || []);
      setLaporan(items);

      // Fetch workflow info untuk cek can_resolve
      if (activeTab === 'pending' && items.length > 0) {
        fetchResolveAbility(items);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setLaporan([]);
    } finally {
      setLoading(false);
    }
  }, [filters, searchTerm, activeTab]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await validasiService.getStats();
      const data = (res as any)?.data || res;
      setStats(data);
    } catch (err) { console.error('Failed to load stats:', err); }
  }, []);

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [fetchData, fetchStats, activeTab]);

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const openModal = (lap: LaporanMasalah, act: ValidasiAction) => { 
    setSelectedLaporan(lap); 
    setAction(act); 
    setCatatan(''); 
  };
  
  const closeModal = () => { 
    setSelectedLaporan(null); 
    setAction(null); 
    setCatatan(''); 
  };

  const handleValidasi = async () => {
    if (!action || !selectedLaporan) return;

    if ((action === 'reject' || action === 'revisi' || action === 'resolve') && !catatan.trim()) {
      toast.error('Catatan wajib diisi');
      return;
    }

    setProcessing(true);
    try {
      // ✅ FIX: Selalu gunakan workflowService jika laporan punya workflow_config_id
      // agar logika validasi terpusat dan konsisten
      if (selectedLaporan.workflow_config_id || action === 'resolve') {
        const wfAction = action === 'revisi' ? 'reject' : action; 
        await workflowService.processValidation(selectedLaporan.id, wfAction as any, catatan);
        toast.success(
          action === 'resolve' 
            ? 'Laporan berhasil ditangani langsung di tingkat Anda'
            : action === 'approve'
              ? 'Laporan disetujui dan diteruskan'
              : 'Laporan telah diproses (dikembalikan ke pelapor)'
        );
      } else {
        await validasiService.validateLevel1(selectedLaporan.id, { action, catatan });
        toast.success(
          action === 'approve'
            ? 'Laporan disetujui dan diteruskan ke Wakil Rektor'
            : action === 'reject'
              ? 'Laporan ditolak dan dikembalikan ke pelapor'
              : 'Laporan dikembalikan ke pelapor untuk diperbaiki'
        );
      }
      closeModal();
      fetchData();
      fetchStats();
    } catch (e: any) {
      toast.error(getErrorMessage(e, 'Gagal memproses validasi'));
      logError('ValidasiLevel1.handleValidasi', e);
    } finally {
      setProcessing(false);
    }
  };

  const toggleSelect = (id: number) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  
  const toggleSelectAll = () => {
    if (selectedIds.length === laporan.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(laporan.map(l => l.id));
    }
  };

  const handleBatchApprove = async () => {
    if (selectedIds.length === 0) return;
    setProcessing(true);
    try {
      await validasiService.batchApproveLevel1(selectedIds);
      toast.success(`${selectedIds.length} laporan berhasil disetujui`);
      setSelectedIds([]);
      fetchData();
      fetchStats();
    } catch (e) { toast.error('Gagal memproses batch validasi'); }
    finally { setProcessing(false); }
  };

  const handleSearch = () => { fetchData(); };

  const getDaysSinceSubmit = (lap: LaporanMasalah) => {
    if (!lap.created_at) return null;
    return Math.floor((Date.now() - new Date(lap.created_at).getTime()) / (1000 * 60 * 60 * 24));
  };

  // Guard: only validators can see this page
  if (!role.canValidateL1) {
    return (
      <div className="space-y-6 animate-in">
        <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Persetujuan Laporan' }]} />
        <Card className="text-center py-12">
          <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-warning" />
          <p className="text-lg font-bold">Akses Terbatas</p>
          <p className="text-sm text-neutral-text-secondary mt-1">Hanya Dekan dan Ketua Lembaga yang dapat mengakses halaman persetujuan laporan ini.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Persetujuan Laporan' }]} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Persetujuan Laporan</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">Laporan yang menunggu persetujuan Anda sebagai Dekan / Ketua Lembaga</p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'pending' && selectedIds.length > 0 && (
            <Button variant="success" size="sm" onClick={handleBatchApprove} loading={processing} icon={<CheckCircle className="w-4 h-4" />}>
              Setujui {selectedIds.length} Laporan
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} icon={<Filter className="w-4 h-4" />}>
            Filter
          </Button>
        </div>
      </div>

      {/* Penjelasan singkat */}
      <div className="p-3 bg-info/5 border border-info/20 rounded-xl flex items-start gap-3">
        <Info className="w-5 h-5 text-info shrink-0 mt-0.5" />
        <div className="text-sm text-info">
          <p className="font-semibold mb-1">Pilihan Tindakan Anda:</p>
          <ul className="text-xs space-y-0.5 list-disc pl-4">
            <li><strong>Setuju</strong> — Teruskan laporan ke Wakil Rektor untuk persetujuan selanjutnya</li>
            <li><strong>Tangani Langsung</strong> — Selesaikan masalah tanpa perlu naik ke Wakil Rektor (jika tersedia)</li>
            <li><strong>Revisi</strong> — Kembalikan ke pelapor untuk diperbaiki dengan catatan</li>
            <li><strong>Tolak</strong> — Tolak laporan dan kembalikan ke pelapor</li>
          </ul>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="cursor-pointer" onClick={() => setActiveTab('pending')}>
            <Card
              className={`!p-4 transition-all ${activeTab === 'pending' ? 'ring-2 ring-warning bg-warning/5 shadow-sm' : 'hover:bg-neutral-bg'}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-warning" />
                </div>
                <div>
                  <p className="text-2xl font-black text-neutral-text-primary">{stats.pending_level1}</p>
                  <p className="text-xs text-neutral-text-secondary">Menunggu Persetujuan</p>
                </div>
              </div>
            </Card></div>
          <div className="cursor-pointer" onClick={() => setActiveTab('history')}>
            <Card
              className={`!p-4 transition-all ${activeTab === 'history' ? 'ring-2 ring-success bg-success/5 shadow-sm' : 'hover:bg-neutral-bg'}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-success" />
                </div>
                <div>
                  <p className="text-2xl font-black text-success">{stats.approved_today_l1}</p>
                  <p className="text-xs text-neutral-text-secondary">Disetujui Hari Ini</p>
                </div>
              </div>
            </Card></div>
          <div className="cursor-pointer" onClick={() => setActiveTab('history')}>
            <Card className="!p-4 transition-all hover:bg-neutral-bg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-danger/10 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-danger" />
                </div>
                <div>
                  <p className="text-2xl font-black text-danger">{stats.rejected_today_l1}</p>
                  <p className="text-xs text-neutral-text-secondary">Ditolak Hari Ini</p>
                </div>
              </div>
            </Card></div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-neutral-border">
        <button
          className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'pending' ? 'border-teal-main text-teal-main bg-teal-main/5' : 'border-transparent text-neutral-text-secondary hover:text-neutral-text-primary'}`}
          onClick={() => setActiveTab('pending')}
        >
          Menunggu ({stats?.pending_level1 || 0})
        </button>
        <button
          className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'history' ? 'border-teal-main text-teal-main bg-teal-main/5' : 'border-transparent text-neutral-text-secondary hover:text-neutral-text-primary'}`}
          onClick={() => setActiveTab('history')}
        >
          <History className="w-4 h-4" />
          Riwayat
        </button>
      </div>

      {/* Search & Filters */}
      {showFilters && (
        <Card className="!p-4 animate-in fade-in slide-in-from-top-1">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <div className="flex gap-2">
                <Input placeholder="Cari judul atau kode..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
                <Button variant="primary" size="sm" onClick={handleSearch} icon={<Search className="w-4 h-4" />}>Cari</Button>
              </div>
            </div>
            <Select
              value={filters.risiko || ''}
              onChange={(value) => setFilters({ ...filters, risiko: value || undefined })}
              options={[
                { value: '', label: 'Semua Risiko' },
                { value: 'rendah', label: 'Rendah' },
                { value: 'sedang', label: 'Sedang' },
                { value: 'tinggi', label: 'Tinggi' },
                { value: 'kritis', label: 'Kritis' },
              ]}
            />
            <Button variant="ghost" size="sm" onClick={() => { setFilters({}); setSearchTerm(''); }}>Reset</Button>
          </div>
        </Card>
      )}

      {/* List */}
      <div className="space-y-4">
        {loading ? (
          <Loading variant="inline" message="Memuat data..." className="py-8" />
        ) : laporan.length > 0 ? (
          <>
            {activeTab === 'pending' && (
              <div className="flex items-center gap-2 px-1">
                <input type="checkbox" checked={selectedIds.length === laporan.length && laporan.length > 0} onChange={toggleSelectAll}
                  className="rounded border-neutral-border text-teal-main focus:ring-teal-light" />
                <span className="text-xs text-neutral-text-secondary">Pilih semua ({laporan.length})</span>
              </div>
            )}
            {laporan.map((lap) => {
              const days = getDaysSinceSubmit(lap);
              const isExpanded = expandedId === lap.id;

              // Untuk tab riwayat: ambil riwayat dari field riwayat_transisi yang diisi backend
              const transisi = activeTab === 'history' ? (lap as any).riwayat_transisi : null;
              const historyAction = transisi?.action;
              const historyKind = !transisi
                ? null
                : (historyAction === 'resolve' || transisi.to_state === 'diselesaikan_lokal')
                  ? 'resolve'
                  : (transisi.to_state === 'validasi_l1' || historyAction === 'approve')
                    ? 'approve'
                    : historyAction === 'revisi'
                      ? 'revisi'
                      : 'reject';
              const historyUi = historyKind === 'approve'
                ? {
                  borderClass: 'border-l-success',
                  badgeClass: 'bg-success/10 text-success',
                  label: 'Disetujui',
                  processedLabel: 'Disetujui',
                }
                : historyKind === 'resolve'
                  ? {
                    borderClass: 'border-l-teal-main',
                    badgeClass: 'bg-teal-main/10 text-teal-main',
                    label: 'Ditangani Langsung',
                    processedLabel: 'Ditangani Langsung',
                  }
                  : historyKind === 'revisi'
                    ? {
                      borderClass: 'border-l-warning',
                      badgeClass: 'bg-warning/10 text-warning',
                      label: 'Revisi',
                      processedLabel: 'Direvisi',
                    }
                    : {
                      borderClass: 'border-l-danger',
                      badgeClass: 'bg-danger/10 text-danger',
                      label: 'Ditolak',
                      processedLabel: 'Ditolak',
                    };

              return (
                <Card
                  key={lap.id}
                  hover
                  className={`border-l-4 transition-all ${activeTab === 'history'
                    ? historyUi.borderClass
                    : 'border-l-warning'
                    }`}
                >
                  <div className="flex items-start gap-4">
                    {activeTab === 'pending' && (
                      <input type="checkbox" checked={selectedIds.includes(lap.id)} onChange={() => toggleSelect(lap.id)} className="mt-1 rounded border-neutral-border text-teal-main focus:ring-teal-light" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-xs font-mono text-teal-main">{lap.kode}</span>
                            <Badge variant="risk" risk={lap.tingkat_risiko} />
                            {activeTab === 'history' && transisi && (
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${historyUi.badgeClass}`}>
                                {historyUi.label}
                              </span>
                            )}
                            {/* Badge Bisa Ditangani Langsung */}
                            {activeTab === 'pending' && canResolveMap[lap.id] && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">
                                Bisa Ditangani Langsung
                              </span>
                            )}
                            {lap.kategori && (
                              <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: lap.kategori?.color }}>
                                {lap.kategori?.nama}
                              </span>
                            )}
                            {activeTab === 'pending' && days !== null && days > 7 && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-warning/10 text-warning font-medium">
                                {days} hari pending
                              </span>
                            )}
                          </div>
                          <Link to={`/laporan/${lap.id}`} className="text-base font-bold text-neutral-text-primary hover:text-teal-main transition-colors">
                            {lap.judul}
                          </Link>
                          <div className="flex items-center gap-4 mt-2 text-xs text-neutral-text-secondary flex-wrap">
                            <span className="flex items-center gap-1"><User className="w-3 h-3" /> {lap.user?.name}</span>
                            <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {lap.unit?.name}</span>
                            {lap.target_selesai && (
                              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Target: {new Date(lap.target_selesai).toLocaleDateString('id-ID')}</span>
                            )}
                            {/* Tanggal diproses untuk tab riwayat */}
                            {activeTab === 'history' && transisi && (
                              <span className="flex items-center gap-1 font-medium text-teal-main">
                                <Clock className="w-3 h-3" />
                                {historyUi.processedLabel}: {new Date(transisi.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                          {/* Catatan dari riwayat transisi */}
                          {activeTab === 'history' && transisi?.notes && (
                            <p className="mt-2 text-xs text-neutral-text-secondary italic bg-neutral-bg p-2 rounded-lg border-l-2 border-neutral-border">
                              "{transisi.notes}"
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => toggleExpand(lap.id)} icon={isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}>
                            {isExpanded ? 'Tutup' : 'Detail'}
                          </Button>
                          {activeTab === 'pending' ? (
                            <>
                              <Button variant="success" size="sm" onClick={() => openModal(lap, 'approve')} icon={<CheckCircle className="w-4 h-4" />}>Setuju</Button>

                              {/* Tombol Tangani Langsung - muncul jika workflow mengizinkan */}
                              {canResolveMap[lap.id] && (
                                <Button variant="primary" size="sm" onClick={() => openModal(lap, 'resolve')}
                                  icon={<Shield className="w-4 h-4" />}>Tangani Langsung</Button>
                              )}

                              <Button variant="outline" size="sm" onClick={() => openModal(lap, 'revisi')} icon={<RotateCcw className="w-4 h-4" />}>Revisi</Button>
                              <Button variant="danger" size="sm" onClick={() => openModal(lap, 'reject')} icon={<XCircle className="w-4 h-4" />}>Tolak</Button>
                            </>
                          ) : (
                            <Link to={`/laporan/${lap.id}`}>
                              <Button variant="primary" size="sm" icon={<Eye className="w-4 h-4" />}>Lihat Detail</Button>
                            </Link>
                          )}
                        </div>
                      </div>

                      {/* Expanded Detail */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-neutral-border space-y-4 animate-in slide-in-from-top-2">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs font-bold text-neutral-text-secondary uppercase tracking-wider mb-1">Deskripsi Masalah</p>
                              <p className="text-sm text-neutral-text-primary line-clamp-4">{lap.deskripsi || <span className="italic text-neutral-text-secondary">Tidak ada deskripsi</span>}</p>
                            </div>
                            <div>
                              <p className="text-xs font-bold text-neutral-text-secondary uppercase tracking-wider mb-1">Dampak</p>
                              <p className="text-sm text-neutral-text-primary line-clamp-4">{lap.dampak || <span className="italic text-neutral-text-secondary">Tidak ada informasi dampak</span>}</p>
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Link to={`/laporan/${lap.id}`}>
                              <Button variant="ghost" size="sm" icon={<Eye className="w-4 h-4" />}>Lihat Detail Lengkap</Button>
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </>
        ) : (
          <Card className="text-center py-16">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: activeTab === 'history' ? 'rgb(var(--color-teal-light)/0.1)' : 'rgb(var(--color-success)/0.1)' }}>
              {activeTab === 'history'
                ? <History className="w-8 h-8 text-teal-main" />
                : <CheckCircle className="w-8 h-8 text-success" />
              }
            </div>
            <p className="text-xl font-black text-neutral-text-primary">
              {activeTab === 'pending' ? 'Semua laporan sudah divalidasi' : 'Belum ada riwayat validasi'}
            </p>
            <p className="text-sm text-neutral-text-secondary mt-2 max-w-xs mx-auto">
              {activeTab === 'pending'
                ? 'Tidak ada laporan baru yang menunggu persetujuan Anda saat ini.'
                : 'Laporan yang telah Anda setujui atau tolak akan muncul di sini.'}
            </p>
            {searchTerm && (
              <Button variant="ghost" size="sm" className="mt-4" onClick={() => { setSearchTerm(''); setFilters({}); }}>Bersihkan Pencarian</Button>
            )}
          </Card>
        )}
      </div>

      {/* Modal */}
      <Modal isOpen={!!selectedLaporan && !!action} onClose={closeModal}
        title={
          action === 'approve' ? 'Setujui & Teruskan ke Wakil Rektor' :
            action === 'reject' ? 'Tolak Laporan' :
              action === 'resolve' ? 'Tangani Langsung di Tingkat Anda' :
                'Kembalikan untuk Diperbaiki'
        }
        size="md"
        footer={<><Button variant="outline" onClick={closeModal}>Batal</Button><Button variant={action === 'approve' ? 'success' : action === 'reject' ? 'danger' : action === 'resolve' ? 'primary' : 'warning'} onClick={handleValidasi} loading={processing} disabled={(action !== 'approve') && !catatan.trim()}>{action === 'approve' ? 'Setujui ke Wakil Rektor' : action === 'reject' ? 'Tolak Laporan' : action === 'resolve' ? 'Tangani Langsung' : 'Kembalikan untuk Revisi'}</Button></>}>
        {selectedLaporan && (
          <div className="space-y-4">
            {/* Info banner */}
            <div className={`p-3 rounded-xl border text-sm font-medium flex items-start gap-3 ${action === 'approve' ? 'bg-success/5 border-success/20 text-success' :
              action === 'reject' ? 'bg-danger/5 border-danger/20 text-danger' :
                action === 'resolve' ? 'bg-info/5 border-info/20 text-info' :
                  'bg-warning/5 border-warning/20 text-warning'
              }`}>
              {action === 'approve' && <CheckCircle className="w-5 h-5 shrink-0 mt-0.5" />}
              {action === 'reject' && <XCircle className="w-5 h-5 shrink-0 mt-0.5" />}
              {action === 'revisi' && <RotateCcw className="w-5 h-5 shrink-0 mt-0.5" />}
              {action === 'resolve' && <Shield className="w-5 h-5 shrink-0 mt-0.5" />}
              <div>
                {action === 'approve' && <p>Anda akan menyetujui laporan ini. Laporan diteruskan ke Wakil Rektor.</p>}
                {action === 'reject' && <p>Laporan akan ditolak dan dikembalikan ke pelapor.</p>}
                {action === 'revisi' && <p>Laporan akan dikembalikan ke pelapor untuk diperbaiki.</p>}
                {action === 'resolve' && (
                  <>
                    <p>Anda akan menangani masalah ini langsung tanpa perlu persetujuan Wakil Rektor dan pembahasan di Rapat Pimpinan.</p>
                    <p className="text-xs mt-1 opacity-80">Wakil Rektor dan Rektor akan menerima pemberitahuan.</p>
                  </>
                )}
              </div>
            </div>

            {/* Report card */}
            <div className="p-4 bg-neutral-bg rounded-xl space-y-2">
              <p className="text-xs font-mono text-teal-main">{selectedLaporan.kode}</p>
              <p className="text-sm font-bold text-neutral-text-primary">{selectedLaporan.judul}</p>
              <div className="flex items-center gap-3 flex-wrap">
                <Badge variant="risk" risk={selectedLaporan.tingkat_risiko} />
                <span className="text-xs text-neutral-text-secondary flex items-center gap-1"><Building2 className="w-3 h-3" /> {selectedLaporan.unit?.name}</span>
                <span className="text-xs text-neutral-text-secondary flex items-center gap-1"><User className="w-3 h-3" /> {selectedLaporan.user?.name}</span>
              </div>
              {selectedLaporan.deskripsi && (
                <p className="text-xs text-neutral-text-secondary mt-2 line-clamp-3">{selectedLaporan.deskripsi}</p>
              )}
            </div>

            {/* Validation path (only for approve) */}
            {action === 'approve' && (
              <div className="flex items-center justify-center gap-2 py-2">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/10 text-success text-xs font-bold">
                  <CheckCircle className="w-3.5 h-3.5" /> Dekan / Ketua Lembaga Setuju
                </div>
                <div className="w-8 h-0.5 bg-neutral-border" />
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-neutral-bg text-neutral-text-secondary text-xs font-bold">
                  <Clock className="w-3.5 h-3.5" /> Menunggu Warek
                </div>
                <div className="w-8 h-0.5 bg-neutral-border" />
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-neutral-bg text-neutral-text-secondary text-xs font-bold">
                  RAPIM
                </div>
              </div>
            )}

            {/* Resolve path */}
            {action === 'resolve' && (
              <div className="flex items-center justify-center gap-2 py-2 flex-wrap">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-info/10 text-info text-xs font-bold">
                  <Shield className="w-3.5 h-3.5" /> Ditangani Dekan / Ketua Lembaga
                </div>
                <div className="w-6 h-0.5 bg-info hidden sm:block" />
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                  <CheckCircle className="w-3.5 h-3.5" /> Selesai
                </div>
              </div>
            )}

            <Textarea
              label={action === 'approve' ? 'Catatan (opsional)' : 'Catatan (wajib)'}
              placeholder={
                action === 'approve' ? 'Tambahkan catatan jika perlu...' :
                  action === 'reject' ? 'Jelaskan alasan penolakan secara detail...' :
                    action === 'resolve' ? 'Jelaskan tindakan yang akan dilakukan untuk menyelesaikan masalah ini...' :
                      'Jelaskan apa yang perlu diperbaiki oleh pelapor...'
              }
              value={catatan}
              onChange={(e) => setCatatan(e.target.value)}
              required={action !== 'approve'}
              rows={4}
            />

            {action !== 'approve' && !catatan.trim() && (
              <p className="text-xs text-danger flex items-center gap-1">
                <XCircle className="w-3 h-3" /> Catatan wajib diisi untuk melanjutkan
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ValidasiLevel1;
